import { Checkbox } from '@/global/components/Checkbox'
import { SCOPES_CONHECIDOS, Scope } from '@acme/core'
import { FormField, FormFieldSelect } from 'global/components/Form'
import React from 'react'
import { useFormContext } from 'react-hook-form'

export const SCOPE_DESCRIPTIONS: Record<
    Scope,
    { label: string; description: string }
> = {
    'registros:read': {
        label: 'Registros de Ponto (leitura)',
        description: 'Consulta marcações e batidas de ponto dos funcionários.',
    },
    'funcionarios:read': {
        label: 'Funcionários (leitura)',
        description:
            'Consulta dados cadastrais de funcionários e vínculos de locais.',
    },
    'calculo:read': {
        label: 'Cálculo e Espelho (leitura)',
        description: 'Consulta apuração do cálculo e espelho de ponto.',
    },
    'saldo-horas:read': {
        label: 'Saldo de Horas (leitura)',
        description: 'Consulta saldo de horas e extrato de banco de horas.',
    },
}

interface CredencialFormProps {
    isEditing?: boolean
    /**
     * Mostra o campo de cota (RPM). A cota é decisão comercial da Farol, não do
     * cliente — só Administrador e Suporte editam, pela tela por linha em
     * Administração. A API descarta o campo de quem não tem alçada, então
     * exibi-lo aqui faria o cliente preencher algo sem efeito.
     */
    podeEditarCota?: boolean
}

export const CredencialForm: React.FC<CredencialFormProps> = ({
    isEditing = false,
    podeEditarCota = false,
}) => {
    const {
        watch,
        setValue,
        formState: { errors },
    } = useFormContext()

    const selectedScopes = (watch('scopes') || []) as Scope[]

    const handleToggleScope = (scope: Scope) => {
        const nextScopes = selectedScopes.includes(scope)
            ? selectedScopes.filter(s => s !== scope)
            : [...selectedScopes, scope]
        setValue('scopes', nextScopes, {
            shouldValidate: true,
            shouldDirty: true,
        })
    }

    const scopesError = errors.scopes?.message as string | undefined

    return (
        <div className="flex flex-col gap-4">
            <div className="w-full">
                <FormField
                    name="nome"
                    label="Nome da Aplicação / Integração"
                    placeholder="Ex: PowerBI Diretoria, ERP Senior, Totvs"
                    required
                />
            </div>

            {(podeEditarCota || isEditing) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {podeEditarCota && (
                        <FormField
                            name="rateLimitRpm"
                            label="Limite de Requisições por Minuto (RPM)"
                            type="number"
                            defaultValue={60}
                            required
                        />
                    )}

                    {isEditing && (
                        <FormFieldSelect
                            name="ativo"
                            label="Status"
                            options={[
                                { label: 'Ativo', value: 'S' },
                                { label: 'Inativo', value: 'N' },
                            ]}
                        />
                    )}
                </div>
            )}

            <div className="flex flex-col gap-2">
                <label className="font-medium text-sm text-foreground">
                    Escopos de Acesso Permitidos *
                </label>
                <div className="flex flex-col gap-2">
                    {SCOPES_CONHECIDOS.map((scope: Scope) => {
                        const info = SCOPE_DESCRIPTIONS[scope]
                        const checked = selectedScopes.includes(scope)
                        return (
                            <div
                                key={scope}
                                className={`flex items-start gap-3 p-3 rounded border transition-colors cursor-pointer ${
                                    checked
                                        ? 'border-primary bg-primary/5'
                                        : 'border-border hover:bg-muted/30'
                                }`}
                                onClick={() => handleToggleScope(scope)}
                            >
                                <Checkbox
                                    checked={checked}
                                    onCheckedChange={() =>
                                        handleToggleScope(scope)
                                    }
                                    onClick={(e: React.MouseEvent) =>
                                        e.stopPropagation()
                                    }
                                />
                                <div className="flex flex-col select-none">
                                    <span className="text-sm font-semibold text-foreground">
                                        {info.label} ({scope})
                                    </span>
                                    <span className="text-xs text-muted-foreground mt-0.5">
                                        {info.description}
                                    </span>
                                </div>
                            </div>
                        )
                    })}
                </div>
                {scopesError && (
                    <p className="text-xs text-destructive mt-1">
                        {scopesError}
                    </p>
                )}
            </div>
        </div>
    )
}
